/* eslint-disable max-len */
import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroupDirective,
  NgForm,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { MuniSupportService } from '@app/core/api/api/muni-support.service';
import { CasePublicModel } from '@app/core/models/case.model';
import { OptionModel } from '@app/core/models/option.model';
import { SurveyData } from '@app/core/models/survey-settings.model';
import {
  CASE_ORIGIN_CODE,
  REGEX_PATTERN,
  SUPPORT_TYPES,
} from '@app/shared/app.constants';
import { ErrorModalComponent } from '@app/shared/components/error-modal/error-modal.component';
import { FeedbackDialogComponent } from '@app/shared/components/feeedback-dialog/feedback-dialog.component';
import { SupportTicketSubmitModalComponent } from '@app/shared/components/support-ticket-submit-modal/support-ticket-submit-modal.component';
import {
  CaseActions,
  CaseSelectors,
  GlobalActions,
  SurveyActions,
  SurveySelectors,
  TwilioSelectors,
} from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

export class SupportPublicErrorMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form && form.submitted;

    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
declare let gtag: Function;

@Component({
  selector: 'app-missed-service',
  templateUrl: './missed-service.component.html',
  styleUrls: ['./missed-service.component.scss'],
})
export class MissedServiceComponent implements OnInit, OnDestroy {
  selectedMunicipality: {
    value: number;
    text: string;
    state: string;
    options: string[];
  } = null;
  subscriptions: Subscription = new Subscription();
  supportCaseTypes: OptionModel[];
  fileIsEncoding = false;
  base64String = '';
  initialValues = null;
  descriptionPlaceholderText =
    'Describe the service that was missed, including the date and location. Any additional information about the missed service is valuable.';
  submitTicketModalRef: MatDialogRef<SupportTicketSubmitModalComponent, any> =
    null;
  feedDialogRef: MatDialogRef<FeedbackDialogComponent, any> = null;
  isExtraSmall: Observable<BreakpointState> = this.breakpointObserver.observe(
    Breakpoints.XSmall
  );
  survey$: Observable<SurveyData>;
  surveyLink: string = null;

  missedService = this.fb.group({
    requestType: [null],
    address: ['', Validators.required],
    city: ['', Validators.required],
    state: ['', Validators.required],
    zipcode: [
      '',
      [Validators.required, Validators.pattern(REGEX_PATTERN.ZIPCODE)],
    ],
    wasteStream: ['', Validators.required],
    equipmentType: ['', Validators.required],
    equipmentSize: [{ value: '', disabled: true }, Validators.required],
    pickupDate: [new Date(Date.now()), Validators.required],
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.pattern(REGEX_PATTERN.EMAIL)]],
    isEmailValidated: [false],
    additionalEmailAddress: ['', [Validators.maxLength(255)]],
    phone: [
      '',
      [Validators.required, Validators.pattern(REGEX_PATTERN.PHONENUMBER)],
    ],
    isPhoneValidated: [false],
    phoneOtp: ['', [Validators.minLength(4), Validators.maxLength(6)]],
    emailOtp: ['', [Validators.minLength(4), Validators.maxLength(6)]],
    fileUpload: [null],
    description: ['', [Validators.required, Validators.maxLength(200)]],
    submit: [''],
  });

  errorMatcher = new SupportPublicErrorMatcher();
  readonly SUPPORT_TYPES = SUPPORT_TYPES;

  isPhoneValidated$: Observable<boolean> = this.store.select(
    TwilioSelectors.selectIsPhoneValidated
  );

  isEmailValidated$: Observable<boolean> = this.store.select(
    TwilioSelectors.selectIsEmailValidated
  );

  constructor(
    private muniSupportService: MuniSupportService,
    public modal: MatDialog,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private recaptchaV3Service: ReCaptchaV3Service,
    public dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private datePipe: DatePipe
  ) {
    this.survey$ = this.store.select(SurveySelectors.getActiveSurveyInfo);
  }

  get missedServiceControls() {
    return this.missedService.controls;
  }

  ngOnInit() {
    // //////////Twilio/////////////////
    const phoneData = localStorage.getItem('phoneVerificationStatus');
    const emailData = localStorage.getItem('emailVerificationStatus');

    if (phoneData) {
      const { phone, isVerified } = JSON.parse(phoneData);

      if (isVerified) {
        this.missedService.controls.isPhoneValidated.setValue(isVerified);
        this.missedService.controls.phone.setValue(phone);
      }
    }

    if (emailData) {
      const { email, isVerified } = JSON.parse(emailData);

      if (isVerified) {
        this.missedService.controls.isEmailValidated.setValue(isVerified);
        this.missedService.controls.email.setValue(email);
      }
    }

    this.subscriptions.add(
      this.isPhoneValidated$.subscribe((isPhoneValidated: boolean) => {
        if (isPhoneValidated) {
          this.missedService.controls.isPhoneValidated.setValue(
            isPhoneValidated
          );
          const phoneNumberField = this.missedService.get('phone');

          localStorage.setItem(
            'phoneVerificationStatus',
            JSON.stringify({
              phone: phoneNumberField.value,
              isVerified: isPhoneValidated,
              verifiedDate: new Date(),
            })
          );
        }
      })
    );

    this.subscriptions.add(
      this.isEmailValidated$.subscribe((isEmailValidated: boolean) => {
        if (isEmailValidated) {
          this.missedService.controls.isEmailValidated.setValue(
            isEmailValidated
          );
          const emailField = this.missedService.get('email');

          localStorage.setItem(
            'emailVerificationStatus',
            JSON.stringify({
              email: emailField.value,
              isVerified: isEmailValidated,
              verifiedDate: new Date(),
            })
          );
        }
      })
    );

    this.missedService.controls.phone.valueChanges.subscribe(newPhone => {
      const { phone, isVerified } = JSON.parse(phoneData);

      if (isVerified) {
        if (phone !== newPhone) {
          this.missedService.controls.isPhoneValidated.setValue(false);
        } else {
          this.missedService.controls.isPhoneValidated.setValue(true);
        }
      }
    });

    this.missedService.controls.email.valueChanges.subscribe(newEmail => {
      const { email, isVerified } = JSON.parse(phoneData);

      if (isVerified) {
        if (email !== newEmail) {
          this.missedService.controls.isEmailValidated.setValue(false);
        } else {
          this.missedService.controls.isEmailValidated.setValue(true);
        }
      }
    });

    /// //////////////////

    this.subscriptions.add(
      this.store
        .select(SurveySelectors.getActiveSurveyInfo)
        .subscribe(survey => {
          if (survey) {
            const surveyStartDate = new Date(
              this.datePipe.transform(
                survey.publicSurveyDateRange?.startDate,
                'yyyy-MM-ddTHH:mm:ssZ'
              )
            );
            const surveyEndDate = new Date(
              this.datePipe.transform(
                survey.publicSurveyDateRange?.endDate,
                'yyyy-MM-ddTHH:mm:ssZ'
              )
            );
            const currentUtcDate = new Date(new Date().toUTCString());
            const currentUtcDateString = currentUtcDate.toISOString();

            if (
              currentUtcDateString >= surveyStartDate.toISOString() &&
              currentUtcDateString <= surveyEndDate.toISOString()
            ) {
              this.surveyLink = survey.publicSurveyDateRange?.surveyLink;
            }
          }
        })
    );

    // Get survey details only if the survey link is null
    if (this.surveyLink === null) {
      this.store.dispatch(SurveyActions.getSurveySettings());
    }

    const selectedMunicipalityId = Number(
      this.route.snapshot.params.municipalityId
    );

    if (selectedMunicipalityId) {
      const municipalities = this.muniSupportService.getMunicipalities();

      this.selectedMunicipality = municipalities.find(
        municipality => municipality.value === selectedMunicipalityId
      );

      if (this.selectedMunicipality) {
        this.missedService.get('city').setValue(this.selectedMunicipality.text);
        this.missedService
          .get('state')
          .setValue(this.selectedMunicipality.state);
        // this.missedService.get('city').disable({ onlySelf: true });// TODO: check this if disabled
        // this.missedService.get('state').disable({ onlySelf: true });
      }
    }

    this.getSupportCaseTypes();
    this.subscribeToCreatedCase();
    this.subscribeToCaseCreationError();
    this.initialValues = this.missedService.value;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  setFileEncoding(fileIsEncoding) {
    this.fileIsEncoding = fileIsEncoding;
  }

  setBase64String(base64String) {
    this.base64String = base64String;
  }

  getSupportCaseTypes() {
    const supportTypeSubscription = this.store
      .select(CaseSelectors.getSupportCaseTypes)
      .subscribe((supportCaseTypes: OptionModel[]) => {
        if (supportCaseTypes.length > 0) {
          this.supportCaseTypes = supportCaseTypes;
        }
      });

    this.subscriptions.add(supportTypeSubscription);
  }

  subscribeToCreatedCase() {
    const createdCaseSubscription = this.store
      .select(CaseSelectors.getCreatedCaseNumber)
      .subscribe(createdCaseNumber => {
        if (createdCaseNumber) {
          gtag('event', 'muni_support_request', {
            case_number: createdCaseNumber,
          });

          // hide spinner when new caseNumber is returned
          this.store.dispatch(GlobalActions.hideSpinner());

          this.submitTicketModalRef = this.modal.open(
            SupportTicketSubmitModalComponent,
            {
              disableClose: true,
              data: {
                caseNumber: createdCaseNumber,
                rerouteUrl: `/muni-support/select-municipality`,
                queryParams: { hasBackButton: true },
              },
            }
          );

          this.submitTicketModalRef.afterClosed().subscribe(() => {
            if (this.surveyLink) this.openFeedbackDialog();
          });
        }
      });

    this.subscriptions.add(createdCaseSubscription);
  }

  // public case form
  subscribeToCaseCreationError() {
    const caseCreationErrorSubscription = this.store
      .select(CaseSelectors.getCaseCreationError)
      .subscribe(caseCreationError => {
        if (caseCreationError) {
          gtag('event', 'support_request_failed', { error: caseCreationError });

          // hide spinner when error is returned
          this.store.dispatch(GlobalActions.hideSpinner());
          this.modal.open(ErrorModalComponent, {
            disableClose: false,
            data: { error: caseCreationError },
          });
          this.modal.afterAllClosed.pipe(take(1)).subscribe(() => {
            this.store.dispatch(CaseActions.clearCaseCreationError());
          });
        }
      });

    this.subscriptions.add(caseCreationErrorSubscription);
  }

  openFeedbackDialog(): void {
    this.feedDialogRef = this.dialog.open(FeedbackDialogComponent, {
      data: { surveyLink: this.surveyLink },
      width: '90%',
      maxWidth: '100vw',
      maxHeight: '100vh',
    });

    const smallFBDialogSubscription = this.isExtraSmall.subscribe(size => {
      if (size.matches) {
        this.feedDialogRef.updateSize(
          'calc(99vw - .3125rem)',
          'calc(99vh - .3125rem)'
        );
        this.feedDialogRef.updatePosition({
          bottom: '.3125rem',
          right: '.3125rem',
        });
      }
    });

    this.feedDialogRef.afterClosed().subscribe(() => {
      smallFBDialogSubscription.unsubscribe();
    });
  }

  onPublicCaseFormSubmit() {
    // the form will contain a mix of valid AND disabled controls
    // make sure the form is not "invalid"
    if (!this.missedService.invalid) {
      let caseTypeName = '';
      const file = this.missedServiceControls.fileUpload.value;

      // set caseTypeName depending on formSupportType
      const caseType = this.supportCaseTypes.find(
        supportCaseType => supportCaseType.id === SUPPORT_TYPES.MISSED_SERVICE
      );

      caseTypeName = caseType.displayName
        ? caseType.displayName
        : caseType.name;

      // build description - always add these
      let description =
        'Name: ' +
        this.missedServiceControls.name.value +
        '\n' +
        'Email: ' +
        this.missedServiceControls.email.value +
        '\n' +
        'Phone: ' +
        this.missedServiceControls.phone.value;

      // add user description at the bottom
      description += '\n\n' + this.missedServiceControls.description.value;

      const newCase: CasePublicModel = {
        municipalityCase: this.selectedMunicipality.value,
        title: `${caseTypeName} - ${this.missedService.controls.name.value}`,
        caseTypeCode: Number(SUPPORT_TYPES.MISSED_SERVICE),
        subCaseTypeCode: null, // This is null if there is no subcase type
        description: description,
        caseOriginCode: CASE_ORIGIN_CODE.CUSTOMER_PORTAL,
        usersName: this.missedServiceControls.name.value,
        phoneNumber: this.missedServiceControls.phone.value,
        emailAddress: this.missedServiceControls.email.value,
        additionalEmailAddress:
          this.missedServiceControls.additionalEmailAddress.value,
        streetAddress: this.missedServiceControls.address.value,
        city: this.missedServiceControls.city.value,
        state: this.missedServiceControls.state.value,
        zip: this.missedServiceControls.zipcode.value,
        wasteStream: this.missedServiceControls.wasteStream.value,
        equipmentType: this.missedServiceControls.equipmentType.value,
        equipmentSize: this.missedServiceControls.equipmentSize.value,
        attachmentFileName: file ? file.name : '',
        attachmentContentType: file ? file.type : '',
        attachmentContent: file ? this.base64String : '',
        caseForCountry: 'US',
        emailNotification: this.missedServiceControls.isEmailValidated.value,
        sms: this.missedServiceControls.isPhoneValidated.value,
      };

      newCase.requestedDate =
        this.missedService.controls.pickupDate.value.toISOString();

      // the response for this takes some time. show spinner until caseNumber is returned
      this.store.dispatch(GlobalActions.showSpinner());

      // Do the captcha action
      const captchaSubscription = this.recaptchaV3Service
        .execute('createCase')
        .subscribe(token => {
          newCase.captchaToken = token;
          this.store.dispatch(
            CaseActions.createCasePublic({ newCase: newCase })
          );
        });

      this.subscriptions.add(captchaSubscription);
    } else {
      Object.keys(this.missedServiceControls).forEach(field => {
        const control = this.missedService.get(field);

        control.markAsTouched({ onlySelf: true });
      });
    }
  }

  onPublicCaseFormCancel() {
    this.missedService.reset(this.initialValues);
    this.missedService.markAsPristine();
    this.missedService.markAsTouched();
  }
}
